import { useSignal, themeParams, miniApp } from '@tma.js/sdk-react'
import { useEffect } from 'react'

export function useTheme() {
  // Ensure themeParams and miniApp are mounted when hook is used
  useEffect(() => {
    if (themeParams.mount.isAvailable()) {
      themeParams.mount()
    }
    if (miniApp.mount.isAvailable()) {
      miniApp.mount()
    }
  }, [])

  const isDark = useSignal(miniApp.isDark)
  const bgColor = useSignal(themeParams.bgColor)
  const textColor = useSignal(themeParams.textColor)
  const hintColor = useSignal(themeParams.hintColor)
  const linkColor = useSignal(themeParams.linkColor)
  const buttonColor = useSignal(themeParams.buttonColor)
  const buttonTextColor = useSignal(themeParams.buttonTextColor)
  const secondaryBgColor = useSignal(themeParams.secondaryBgColor)
  
  const darkMode = isDark ?? false
  
  return {
    isDark: darkMode,
    bgColor: bgColor || '#ffffff',
    textColor: textColor || '#000000',
    hintColor: hintColor || '#999999',
    linkColor: linkColor || '#0088cc',
    buttonColor: buttonColor || '#0088cc',
    buttonTextColor: buttonTextColor || '#ffffff',
    secondaryBgColor: secondaryBgColor || '#f4f4f5',
    
    // Dynamic colors based on theme
    cardBg: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#f8f9fa',
    cardBorder: darkMode ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0',
    cardHoverBg: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#e8e9ea',
    inputBg: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff',
    inputBorder: darkMode ? 'rgba(255, 255, 255, 0.2)' : '#d0d0d0',
    inputBgSubtle: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
    inputBorderSubtle: darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
    cardBgLight: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
    
    // Brand colors (app blue — matches Telegram button / membership heroes)
    brand: {
      light: 'rgba(0, 136, 204, 0.15)',
      medium: 'rgba(0, 136, 204, 0.2)',
      dark: 'rgba(0, 136, 204, 0.25)',
      solid: darkMode ? 'rgba(0, 136, 204, 1)' : 'rgba(0, 136, 204, 0.9)',
      bg: darkMode ? 'rgba(0, 136, 204, 0.08)' : 'rgba(0, 136, 204, 0.05)',
      border: darkMode ? 'rgba(0, 136, 204, 0.3)' : 'rgba(0, 136, 204, 0.2)',
    },
    
    // Accent colors (blue - secondary actions)
    accent: {
      light: 'rgba(0, 136, 204, 0.2)',
      medium: 'rgba(0, 136, 204, 0.25)',
      text: darkMode ? 'rgb(96, 165, 250)' : 'rgb(37, 99, 235)',
      border: darkMode ? 'rgba(0, 136, 204, 0.5)' : 'rgba(0, 136, 204, 0.4)',
    },
    
    // Card shadows
    shadow: {
      card: darkMode 
        ? '0 2px 8px rgba(0, 136, 204, 0.15)' 
        : '0 2px 8px rgba(0, 136, 204, 0.1)',
    },
    
    // Modal colors
    modal: {
      backdrop: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.6)',
      bg: darkMode ? 'rgba(30, 30, 30, 1)' : '#ffffff',
      border: darkMode ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0',
    },
    
    // Text colors
    text: {
      primary: textColor || (darkMode ? '#ffffff' : '#000000'),
      secondary: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
      muted: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    },
  }
}
